import {
  type MutableRefObject,
  type ReactNode,
  useCallback,
  useMemo,
} from "react";
import {
  BottomSheetContentEnum,
  useChatInternalContext,
} from "@b2bportal/core-chat";
import { useChatSupport } from "@hopper-b2b/self-serve";
import { Box, SwipeableDrawer } from "@material-ui/core";
import { ContactSupportDetails } from "@tenant/chat";
import clsx from "clsx";
import { noop } from "lodash-es";

import { CSATForm } from "../CSATForm";

import styles from "./ChatWindowBottomSheet.module.scss";

export interface IChatWindowBottomSheetProps {
  chatWindowRef: MutableRefObject<HTMLDivElement>;
}

const ChatWindowBottomSheet = (
  props: IChatWindowBottomSheetProps
): JSX.Element => {
  const { chatWindowRef } = props;
  const {
    bottomSheetContent,
    chatProvider,
    conversationId,
    closeChatWindow,
    setBottomSheetContent,
  } = useChatInternalContext();
  const { generalSupportChat } = useChatSupport();

  // TODO: Move to useContactSupportDetails after contexts are migrated to core-chat
  const startGeneralSupportChat = useCallback(() => {
    if (conversationId != null && chatProvider != null) {
      generalSupportChat?.({
        conversationId,
        source: chatProvider.chatTransferSource,
      });
    }
  }, [chatProvider, conversationId, generalSupportChat]);

  const ModalContent = useMemo<ReactNode>(() => {
    switch (bottomSheetContent) {
      case BottomSheetContentEnum.CSATFormResolved:
        return <CSATForm onConversationEnd={closeChatWindow} />;
      case BottomSheetContentEnum.ContactSupport:
        return <ContactSupportDetails onStartChat={startGeneralSupportChat} />;
      case BottomSheetContentEnum.Closed:
      default:
        return null;
    }
  }, [bottomSheetContent, closeChatWindow, startGeneralSupportChat]);

  function closeBottomSheet() {
    setBottomSheetContent(BottomSheetContentEnum.Closed);
  }

  return (
    <SwipeableDrawer
      disableDiscovery
      disablePortal
      disableSwipeToOpen
      BackdropProps={{
        className: styles.bottomSheetBackdrop,
      }}
      ModalProps={{
        container: chatWindowRef.current,
      }}
      anchor="bottom"
      classes={{
        paper: styles.bottomSheetPaper,
        root: styles.chatWindowBottomSheet,
      }}
      onClose={closeBottomSheet}
      onOpen={noop}
      open={bottomSheetContent !== BottomSheetContentEnum.Closed}
    >
      <Box
        className={clsx("bottom-sheet-dragbar", styles.bottomSheetDragbar)}
      />
      {ModalContent}
    </SwipeableDrawer>
  );
};

export default ChatWindowBottomSheet;
